import { CalendarIcon, DatabaseIcon } from '@heroicons/react/outline'
import { FC } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { useIntl } from 'lib/intl-utils'
import sellmonitorFeatures from 'components/landing/lib/sellmonitorFeatures'
import UseCasesCards from 'components/landing/sellmonitor/blocks/UseCasesCards'
import SellmonitorFeatures from 'components/landing/sellmonitor/blocks/SellmonitorFeatures'
import ExtensionPlug from 'components/landing/sellmonitor/blocks/ExtensionPlug'
import Features from 'components/landing/sellmonitor/blocks/Features'
import Advantages from 'components/landing/sellmonitor/blocks/Advantages'
import Pricing from 'components/landing/sellmonitor/blocks/Pricing'
import Faq from 'components/landing/sellmonitor/blocks/Faq'
import Cta from 'components/landing/sellmonitor/blocks/Cta'
import featureTranslations from './featureTranslations'
import featureImages from './featureImages'

const contentByLanguage = {
  en: {
    scenarios: {
      title: 'How will Sellscreen help?',
      items: [
        {
          name: 'Find your niche and products',
          cases: [
            'Assess demand, seasonality and competition',
            'Compare competitor sales and prices',
            'Find products to launch or expand your range',
          ],
        },
        {
          name: 'Manage your stores',
          cases: [
            'Track revenue, expenses and profit by product',
            'Calculate unit economics before purchasing stock',
            'Plan supplies using stock levels and demand',
          ],
        },
        {
          name: 'Reduce manual work',
          cases: [
            'Manage prices and advertising bids automatically',
            'Reply to reviews using your own rules',
            'Find keywords and create product descriptions with AI',
          ],
        },
      ],
    },
    heading: 'Sellscreen features',
    buttonTitle: 'Learn more',
    extension: {
      title: 'Try the free Sellscreen extension',
      description:
        'View marketplace analytics directly on product pages with the Sellscreen browser extension.',
    },
    tools: {
      tagline: 'Smart tools to grow your sales',
      description: 'Spend less time on routine tasks and more time growing your business',
      buttonTitle: 'Learn more',
      features: [
        {
          tagline: 'Business analytics',
          description:
            'Track your business metrics, revenue, expenses and profit in one dashboard.',
          image: '/next/img/landing/zh/tools/business-small-localized.png',
        },
        {
          tagline: 'Supply planning',
          description:
            'Calculate replenishment quantities using sales history and the geography of orders.',
        },
        {
          tagline: 'SEO optimization',
          description:
            'Find relevant keywords using competitor data and improve your product descriptions.',
        },
        {
          tagline: 'AI product descriptions',
          description:
            'Create product descriptions with AI and include the keywords your customers search for.',
          image: '/next/img/landing/zh/tools/description-generation-localized.png',
        },
        {
          tagline: 'Bid management',
          description:
            'Automate advertising bids and choose promotion hours using performance statistics.',
          image: '/next/img/landing/zh/tools/bidder-localized.png',
        },
        {
          tagline: 'Automated review replies',
          description:
            'Set up reply templates and rules to respond to customer reviews automatically.',
        },
        {
          tagline: 'Price management',
          description: 'Adjust prices using stock levels, schedules and competitor prices.',
          image: '/next/img/landing/zh/tools/repricer-localized.png',
        },
      ],
    },
    advantages: {
      title: 'Why choose Sellscreen?',
      description: 'Marketplace data and tools for informed business decisions',
      items: [
        {
          name: 'Data for business decisions',
          description:
            'Analyze products, categories and competitors using detailed marketplace data.',
          icon: DatabaseIcon,
          class: 'bg-gray-500',
        },
        {
          name: 'Working since 2019',
          description: 'Explore historical data to understand trends and seasonal changes.',
          icon: CalendarIcon,
          class: 'bg-red-500',
        },
      ],
    },
  },
  zh: {
    scenarios: {
      title: 'Sellscreen 有何帮助？',
      items: [
        {
          name: '寻找市场机会与商品',
          cases: [
            '评估需求、季节性与竞争情况',
            '比较竞争对手的销量与价格',
            '寻找适合起步或扩充产品线的商品',
          ],
        },
        {
          name: '管理店铺经营',
          cases: ['按商品追踪销售额、费用与利润', '采购前计算单品利润', '结合库存与需求规划补货'],
        },
        {
          name: '减少重复工作',
          cases: [
            '自动管理价格与广告出价',
            '按照自定义规则回复评论',
            '寻找关键词并用 AI 生成商品描述',
          ],
        },
      ],
    },
    heading: 'Sellscreen 功能',
    buttonTitle: '了解更多',
    extension: {
      title: '试用免费的 Sellscreen 浏览器插件',
      description: '使用 Sellscreen 浏览器插件，直接在商品页面查看平台分析数据。',
    },
    tools: {
      tagline: '智能工具助力提升销量',
      description: '减少日常重复工作，专注业务增长',
      buttonTitle: '了解更多',
      features: [
        {
          tagline: '店铺经营分析',
          description: '在一个看板中追踪经营指标、销售额、费用与利润。',
          image: '/next/img/landing/zh/tools/business-small-localized.png',
        },
        { tagline: '补货规划', description: '根据销售历史与订单地域分布计算补货数量。' },
        { tagline: 'SEO 优化', description: '通过竞品数据寻找相关关键词，优化商品描述。' },
        {
          tagline: 'AI 商品描述',
          description: '使用 AI 生成商品描述，并加入客户搜索的关键词。',
          image: '/next/img/landing/zh/tools/description-generation-localized.png',
        },
        {
          tagline: '广告管理',
          description: '自动调整广告出价，并根据效果数据选择推广时段。',
          image: '/next/img/landing/zh/tools/bidder-localized.png',
        },
        { tagline: '自动回复评论', description: '设置回复模板与规则，自动回复客户评论。' },
        {
          tagline: '价格管理',
          description: '结合库存、时间安排与竞品价格调整定价。',
          image: '/next/img/landing/zh/tools/repricer-localized.png',
        },
      ],
    },
    advantages: {
      title: '为什么选择 Sellscreen？',
      description: '通过平台数据与工具做出有依据的经营决策',
      items: [
        {
          name: '支持经营决策的数据',
          description: '通过详细的平台数据分析商品、类目与竞争对手。',
          icon: DatabaseIcon,
          class: 'bg-gray-500',
        },
        {
          name: '自 2019 年起运营',
          description: '利用历史数据了解市场趋势与季节变化。',
          icon: CalendarIcon,
          class: 'bg-red-500',
        },
      ],
    },
  },
}

const SellscreenIndexPage: FC = () => {
  const ctx = useUserContext()
  const { lang } = useIntl()

  if (ctx.theme.hostType !== 'sellscreen') return null

  const content = contentByLanguage[lang === 'en' ? 'en' : 'zh']
  const features = sellmonitorFeatures
    .filter(({ malls }) => malls.includes('wb'))
    .map((feature) => ({
      ...feature,
      title:
        featureTranslations[feature.title]?.[lang === 'en' ? 0 : 1] ??
        featureTranslations[feature.title]?.[1] ??
        '',
      mainImage: featureImages[feature.title],
      benefit:
        featureTranslations[feature.benefit]?.[lang === 'en' ? 0 : 1] ??
        featureTranslations[feature.benefit]?.[1] ??
        '',
      link: undefined,
      items: feature.items
        .filter(
          ({ description }) =>
            ![
              'Бот в Telegram и Макс с уведомлениями о продажах, выручке и заказах',
              'Генерация и редактирование изображений для карточки товара',
              'Генерация видео для карточки товара по текстовому описанию или изображению',
              'ИИ-ассистент: вопросы о сервисе, идеи для товаров и помощь с расчётами',
              'ИИ-аудит изображений, описания и отзывов товара с рекомендациями по улучшению',
            ].includes(description)
        )
        .map((item) => ({
          ...item,
          description:
            featureTranslations[item.description]?.[lang === 'en' ? 0 : 1] ??
            featureTranslations[item.description]?.[1] ??
            '',
          image: featureImages[item.description],
          link: undefined,
        }))
        .filter(({ description }) => Boolean(description)),
    }))
    .filter(({ title, benefit }) => Boolean(title && benefit))

  return (
    <>
      <UseCasesCards className="!pt-12" content={content.scenarios} />
      {features.length > 0 && (
        <SellmonitorFeatures
          id="features"
          mall="wb"
          showMall={false}
          alignCenter
          features={features}
          heading={content.heading}
          language={lang === 'en' ? 'en' : 'zh'}
          buttonTitle={content.buttonTitle}
        />
      )}
      <ExtensionPlug
        {...content.extension}
        imageAlt="Sellscreen Extension"
        image="/next/img/landing/zh/extension-localized.png"
        href="https://chromewebstore.google.com/detail/sellscreen/kbcjhdbnfdinccjhdcicdmpflamjflok"
      />
      <Features content={content.tools} />
      <Advantages {...content.advantages} />
      <Pricing />
      <Faq className="!pb-0" />
      <Cta />
    </>
  )
}

export default SellscreenIndexPage
